import { useEffect, useRef } from 'react'
import Placeholder from '@tiptap/extension-placeholder'
import { EditorContent, JSONContent, useEditor } from '@tiptap/react'
import StarterKit from '@tiptap/starter-kit'

import { cn } from '@/lib/utils'
import { PlotEmbedNode } from './PlotEmbedNode'
import { QuoteEmbedNode } from './QuoteEmbedNode'
import { QuotesStripEmbedNode } from './QuotesStripEmbedNode'
import {
  ArtifactEditorDoc,
  editorDocToMarkdown,
  parseEmbedTokens,
  segmentsToEditorDoc,
} from './parseEmbedTokens'

export interface ArtifactMdEditorProps {
  value: string
  onChange?: (next: string) => void
  placeholder?: string
  editable?: boolean
  className?: string
}

export function ArtifactMdEditor(props: ArtifactMdEditorProps) {
  const {
    value,
    onChange,
    placeholder = 'Write the artifact…',
    editable = true,
    className,
  } = props

  const lastEmittedRef = useRef(value)
  const onChangeRef = useRef(onChange)
  onChangeRef.current = onChange

  const editor = useEditor({
    immediatelyRender: false,
    editable,
    extensions: [
      StarterKit,
      Placeholder.configure({ placeholder }),
      PlotEmbedNode,
      QuoteEmbedNode,
      QuotesStripEmbedNode,
    ],
    content: segmentsToEditorDoc(parseEmbedTokens(value)),
    onCreate({ editor: nextEditor }) {
      lastEmittedRef.current = editorDocToMarkdown(
        nextEditor.getJSON() as ArtifactEditorDoc,
      )
    },
    onUpdate({ editor: nextEditor }) {
      const next = editorDocToMarkdown(
        nextEditor.getJSON() as ArtifactEditorDoc,
      )
      if (next === lastEmittedRef.current) {
        return
      }
      lastEmittedRef.current = next
      onChangeRef.current?.(next)
    },
  })

  useEffect(() => {
    if (!editor) {
      return
    }

    if (value === lastEmittedRef.current) {
      return
    }

    lastEmittedRef.current = value
    editor.commands.setContent(
      segmentsToEditorDoc(parseEmbedTokens(value)) as JSONContent,
    )
  }, [editor, value])

  useEffect(() => {
    if (!editor) {
      return
    }

    editor.setEditable(editable)
  }, [editable, editor])

  return (
    <div
      className={cn(
        'min-h-[12rem] rounded-md border border-input bg-background px-3 py-2 text-base leading-[1.7] text-foreground',
        'ring-offset-background focus-within:outline-none focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2',
        '[&_.tiptap]:min-h-[10rem] [&_.tiptap]:outline-none',
        '[&_.tiptap_strong]:font-semibold [&_.tiptap_strong]:text-foreground',
        '[&_.tiptap_h1]:mb-1 [&_.tiptap_h1]:mt-4 [&_.tiptap_h1]:font-sans [&_.tiptap_h1]:text-base [&_.tiptap_h1]:font-semibold [&_.tiptap_h1]:leading-[1.7] [&_.tiptap_h1]:text-foreground [&_.tiptap_h1:first-child]:mt-0',
        '[&_.tiptap_h2]:mb-1 [&_.tiptap_h2]:mt-4 [&_.tiptap_h2]:font-sans [&_.tiptap_h2]:text-base [&_.tiptap_h2]:font-semibold [&_.tiptap_h2]:leading-[1.7] [&_.tiptap_h2]:text-foreground [&_.tiptap_h2:first-child]:mt-0',
        '[&_.tiptap_h3]:mb-1 [&_.tiptap_h3]:mt-4 [&_.tiptap_h3]:font-sans [&_.tiptap_h3]:text-base [&_.tiptap_h3]:font-semibold [&_.tiptap_h3]:leading-[1.7] [&_.tiptap_h3]:text-foreground [&_.tiptap_h3:first-child]:mt-0',
        '[&_.tiptap_h4]:mb-1 [&_.tiptap_h4]:mt-4 [&_.tiptap_h4]:font-sans [&_.tiptap_h4]:text-base [&_.tiptap_h4]:font-semibold [&_.tiptap_h4]:leading-[1.7] [&_.tiptap_h4]:text-foreground [&_.tiptap_h4:first-child]:mt-0',
        '[&_.tiptap_p]:my-4 [&_.tiptap_p:first-child]:mt-0 [&_.tiptap_p:last-child]:mb-0',
        className,
      )}
      data-print-hide
      data-testid="artifact-md-editor"
    >
      <EditorContent editor={editor} />
    </div>
  )
}
